// @mui

import { Avatar, Grid, Link, Stack, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { Roles } from 'src/types/User';
import { formatPetTypeAndBreed } from 'src/utils/formatPetTypeAndBreed';
import { mapCategoryNameToJobTitle } from 'src/utils/mapCategoryNameToJobTitle';
import { GetVisitById } from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';


// ---------------------------------------------------------------------

type OrganizationVisitModalDetailsMembersProps = {
  visit?: GetVisitById;
};

export default function OrganizationVisitModalDetailsMembers({
  visit,
}: OrganizationVisitModalDetailsMembersProps) {
  const theme = useTheme();
  const { selectedRole } = useOrganizationContext();
  const isProfessionalRole = selectedRole === Roles.professional;

  if (!visit) {
    return null;
  }

  return (
    <Grid
      container
      sx={{
        gap: { xs: 1, sm: 1, md: 1, lg: 0, xl: 0 },
        alignItems: 'center',
        mb: 3,
      }}
    >
      <Grid
        sx={{
          gap: 2,
          p: 2,
          bgcolor: theme.palette.primary.lighter,
          borderRadius: 2,
          flexDirection: 'row',
        }}
        item
        xs={12}
        sm={12}
        md={12}
        lg={5.5}
      >
        <Stack
          sx={{
            gap: 2,
            flexDirection: 'row',
            alignItems: 'center',
            flex: 1,
          }}
        >
          <Avatar
            src={visit?.profile?.avatar || '/assets/avatars/avatar4.png'}
            sx={{ width: theme.spacing(5), height: theme.spacing(5) }}
          />
          <Stack
            sx={{
              gap: theme.spacing(0.5),
            }}
          >
            {visit?.profile?.id ? (
              <Link
                component={RouterLink}
                to={PATH_DASHBOARD.general.patients.history(visit.profile.id)}
                color="text.primary"
                underline="always"
              >
                {visit.profile.name}
              </Link>
            ) : (
              <Typography color="text.primary">
                {visit?.profile?.name}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              {formatPetTypeAndBreed(
                visit?.profile?.petCategory,
                visit?.profile?.breed,
              )}
            </Typography>
          </Stack>
        </Stack>
      </Grid>
      <Grid
        item
        alignItems="center"
        justifyContent="center"
        xs={12}
        sm={12}
        md={12}
        lg
        xl
      >
        <Typography
          sx={{
            fontSize: { xs: '20px', sm: '20px', md: '24px' },
            textAlign: 'center',
          }}
        >
          z
        </Typography>
      </Grid>
      <Grid
        item
        sx={{
          gap: 2,
          p: 2,
          bgcolor: theme.palette.primary.lighter,
          borderRadius: 2,
          flexDirection: 'row',
        }}
        xs={12}
        sm={12}
        md={12}
        lg={5.5}
      >
        <Stack
          sx={{
            gap: 2,
            flexDirection: 'row',
            alignItems: 'center',
            flex: 1,
          }}
        >
          <Avatar
            src={
              isProfessionalRole
                ? visit?.user?.avatar || '/assets/avatars/avatar.png'
                : visit?.professional?.avatar || '/assets/avatars/avatar.png'
            }
            sx={{ width: theme.spacing(5), height: theme.spacing(5) }}
          />

          <Stack
            sx={{
              gap: theme.spacing(0.5),
            }}
          >
            {!isProfessionalRole ? (
              <Link
                component={RouterLink}
                to={PATH_DASHBOARD.general.professionals.calendar(
                  visit?.professional?.id || '',
                )}
                color="text.primary"
                underline="always"
              >
                {`${visit?.professional?.firstName} ${visit?.professional?.lastName}`}
              </Link>
            ) : (
              <Typography>
                {visit?.user?.firstName} {visit?.user?.lastName}
              </Typography>
            )}

            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              {isProfessionalRole
                ? translateKey("common.owner")
                : visit?.professional?.categories
                    .map((category) => mapCategoryNameToJobTitle(category))
                    .join(', ')}
            </Typography>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
}
